import * as THREE from "three";

/**
 * Build a connection graph from nova lines data
 * Maps each point to an array of connected point IDs
 * @param {Array} lines - Array of line objects with startPoint and endPoint
 * @param {Array} novaPoints - Array of nova point objects with {id, x, y, z, point}
 * @returns {Object} Connection graph: { pointId: [connectedPointId1, connectedPointId2, ...] }
 */
export const buildPointConnections = (lines, novaPoints) => {
  if (!lines || !novaPoints || lines.length === 0 || novaPoints.length === 0) {
    return {};
  }

  const connections = {};

  // Initialize connections object for all points
  novaPoints.forEach(point => {
    connections[point.id] = [];
  });

  // Build connections from lines
  lines.forEach(line => {
    if (!line.startPoint || !line.endPoint) return;

    // Find the nova point IDs that correspond to this line's endpoints
    const startPointId = findPointId(line.startPoint, novaPoints);
    const endPointId = findPointId(line.endPoint, novaPoints);

    if (startPointId !== null && endPointId !== null && startPointId !== endPointId) {
      // Add bidirectional connection
      if (!connections[startPointId].includes(endPointId)) {
        connections[startPointId].push(endPointId);
      }
      if (!connections[endPointId].includes(startPointId)) {
        connections[endPointId].push(startPointId);
      }
    }
  });

  return connections;
};

/**
 * Find the nova point ID that matches the given coordinates
 * @param {Object} point - Point with x, y, z coordinates
 * @param {Array} novaPoints - Array of nova point objects
 * @returns {number|null} The ID of the matching point, or null if not found
 */
const findPointId = (point, novaPoints) => {
  const matchingPoint = novaPoints.find(
    np => np.x === point.x && np.y === point.y && np.z === point.z
  );
  return matchingPoint ? matchingPoint.id : null;
};

/**
 * Get connected points for a given point
 * @param {number} pointId - The ID of the current point
 * @param {Object} connections - The connections graph
 * @param {Array} novaPoints - Array of all nova points
 * @returns {Array} Array of connected nova point objects
 */
export const getConnectedPoints = (pointId, connections, novaPoints) => {
  if (!connections || !connections[pointId] || !novaPoints) {
    return [];
  }

  const connectedIds = connections[pointId];
  return novaPoints.filter(point => connectedIds.includes(point.id));
};

/**
 * Find the next guided points based on line connections
 * @param {Object|Vector3} lastPoint - The last clicked point (Vector3 with x, y, z)
 * @param {Object} connections - The connections graph
 * @param {Array} remainingPoints - Array of remaining nova points
 * @param {Array} allNovaPoints - Array of ALL nova points (including used ones)
 * @returns {Array} Array of connected nova point objects to highlight
 */
export const findNextGuidedPoints = (lastPoint, connections, remainingPoints, allNovaPoints) => {
  if (!lastPoint || !connections || !remainingPoints || remainingPoints.length === 0) {
    return [];
  }

  // Find the nova point that matches the last point from ALL points (not just remaining)
  // Because the last point was just used, it won't be in remainingPoints
  const searchPoints = allNovaPoints || remainingPoints;
  const lastNovaPoint = searchPoints.find(
    np => np.x === lastPoint.x && np.y === lastPoint.y && np.z === lastPoint.z
  );

  if (!lastNovaPoint) {
    return [];
  }

  // Get connected points for this point
  const connectedPointIds = connections[lastNovaPoint.id] || [];

  // Filter to only include points that are still remaining (not used yet)
  const guidedPoints = remainingPoints.filter(point =>
    connectedPointIds.includes(point.id)
  );

  return guidedPoints;
};
